import {Fragment, useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {NotificationManager} from 'react-notifications';
import {useDispatch, useSelector} from 'react-redux';
import cloneDeep from 'lodash.clonedeep';
import omitBy from 'lodash.omitby';
import {BllinkLogger} from "../../../common/utils/bllink_loggers";
import { constants } from "../../../common/constants/constants";
import {StyledTenantsList} from './tenantList.styled';
import Search from '../../../images/search.svg';
import PrintPdfDropdown from '../../../common/export_documents/PrintPdfDropdown';
import Table from '../../../common/table/TableFunctional';
import {hideLoader, showLoader} from '../../../store/common/slice';
import {fetchManagerBuildingData, getBuildingTitle} from '../../../store/buildings/actions';
import {isEditableTableValid, sortApartmentNumbersForTable} from '../../../common/shared/statelessMethods';
import {GAEvent} from '../../../common/google_analytics/google_analytics';
import UploadDownloadToolBar from '../../paymentSettings/uploadDownloadToolBar';
import DragAndDrop from '../../../common/simple/dragAndDrop';
import {isAdminSelector} from '../../../store/auth/selectors';
import XLSX from 'xlsx';

import 'react-notifications/lib/notifications.css';
import BllinkUtils from '../../../common/utils/utils';
import {useAccessToken} from '../../../common/hooks/useAccessToken';
import {StyledActionsBar} from '../report.styled';
import { StyledPdfWrapper } from '../debtList/PdfWrapper.styled';
import { fetchBuildingData } from '../../../store/buildings/actions';
import PdfTenantRow from './PdfTenantRow';
import { PageTitle } from '../../../common/PageTitle';

export default function TenantList({match, history}) {
    const {t, i18n} = useTranslation();
    const {buildingID} = match.params;
    const dispatch = useDispatch();
    const client = useAccessToken();

    const pdfContainer = useRef();

    const userCheck = useSelector((state) => state.auth.userCheck);
    const titleData = useSelector((state) => state.buildings.titleData);
    const managerData = useSelector(state => state.buildings.managerData);
    const isAdmin = useSelector(isAdminSelector);
    const ltr = useSelector(state => state.common.ltrEnabled);

    const [tenants, setTenants] = useState([]);
    const [tableData, setTableData] = useState([]);

    const [skipPageReset, setSkipPageReset] = useState(false);
    const [filters, setFilters] = useState({});

    const [pdfContainerActive, setPdfContainerActive] = useState(false);
    const pdfContainerActivate = () => setPdfContainerActive(true);

    const titleRow = [{
        ApartmentNumber: t('reports.apartmentName'),
        OwnerName: t('reports.ownerName'),
        OwnerPhone: t('reports.ownerPhone'),
        OwnerEmail: t('reports.ownerEmail'),
        RenterName: t('reports.renterName'),
        RenterPhone: t('reports.renterPhone'),
        RenterEmail: t('reports.renterEmail'),
        monthlyFee: t('reports.monthlyFee')
    }];

    document.title = t('titles.tenant_list');

    useEffect(() => {
        const tenantsList = loadTenants();
        setTenants(tenantsList);
        setTableData(tenantsList);
    }, [managerData]);

    useEffect(() => {
        setSkipPageReset(false);
    }, [tenants])

    const loadTenants = () => {
        let data;
        const payload = managerData;
        if (!(payload?.tenantsAsRows || payload?.building?.tenantsAsRows)) {
            BllinkLogger.info(`TenantListTable rows are empty`);
            return;
        }
        data = cloneDeep(payload.tenantsAsRows) || cloneDeep(payload.building?.tenantsAsRows);
        data = data.map(item => {
            return {
                ...item,
                ...item.tenantListData,
                errors: {}
            };
        });
        BllinkLogger.info(`TenantListTableTable rows are `, data);
        return data.sort(sortApartmentNumbersForTable);
    };

    const getDiff = (data1, data2) => {
        return omitBy(data1, (a, b) => {
            return data2[b] === a;
        });
    };

    const renameKeys = (obj, newKeys) => {
        const keyValues = Object.keys(obj).map(key => {
            const newKey = newKeys[key] || key;
            return {[newKey]: obj[key]};
        });
        return Object.assign({}, ...keyValues);
    };

    const saveChanges = async () => {
        dispatch(showLoader());
        let propsToEdit = {};
        const diff = getDiff(tableData, tenants);

        for (let item in diff) {
            propsToEdit[item] = getDiff(diff[item], tenants[item]);
            delete propsToEdit[item].errors;
        }

        if (Object.values(propsToEdit).length) {
            const payload = {
                tenantList: []
            };
            Object.entries(propsToEdit).forEach(item => {
                const renamedData = renameKeys(item[1], {
                    RenterName: 'Name',
                    RenterEmail: 'Email',
                    RenterPhone: 'Phone',
                    monthlyFee: 'fee',
                })
                payload.tenantList.push({
                    ApartmentNumber: tenants[item[0]].ApartmentNumber,
                    ...renamedData
                });
            });

            GAEvent(constants.GoogleAnalytics.Events.editTenants, 'editTenantsDataEvent', payload, 'editTenantsDataStart');
            const response = await client.post(`/managers/buildings/${buildingID}`, payload);
            BllinkLogger.info(`response edit tenants`, response);

            if (response.updated >= 0) {
                NotificationManager.success(t('reports.tenantsSaved'), '', 5000);
                GAEvent(constants.GoogleAnalytics.Events.editTenants, 'editTenantsDataEvent', payload, 'editTenantsDataSuccess');
                // reload data (forceNoCache - to avoid caching issues after data change)
                // await dispatch(fetchManagerBuildingData({buildingID, options: {urlParams: {forceNoCache: true}}}));
                // const tenantsList = await loadTenants();
                // setTenants(tenantsList);
                // setTableData(tenantsList);
            }
        } else NotificationManager.info(t('reports.nothingToSave'), '', 3000);

        dispatch(hideLoader());
    }

    const columnsFinal = () => {
        let columns =  [
            {
                Header: t('reports.apartmentName'),
                accessor: 'ApartmentNumber',
                Cell: props => (
                    <a href={`/${constants.pages.newApartmentReport}/${buildingID}/${props.value}`}>{props.value}</a>
                )
            },
            {
                Header: t('reports.ownerName'),
                accessor: 'OwnerName'
            },
            {
                Header: t('reports.ownerPhone'),
                accessor: 'OwnerPhone',
                minWidth: 100
            },
            {
                Header: t('reports.ownerEmail'),
                accessor: 'OwnerEmail',
                minWidth: 120
            },
            {
                Header: t('reports.renterName'),
                accessor: 'RenterName',
                minWidth: 80
            },
            {
                Header: t('reports.renterPhone'),
                accessor: 'RenterPhone',
                minWidth: 100
            },
            {
                Header: t('reports.renterEmail'),
                accessor: 'RenterEmail',
                minWidth: 120
            }
        ]

        if (isAdmin){
            columns.push(
                {
                    Header: t('reports.monthlyFee'),
                    accessor: 'monthlyFee'
                })
        }else{
            columns.push(
                {
                    Header: t('reports.monthlyFee'),
                    accessor: 'monthlyFee',
                    Cell: props => (
                        <span exceltext={props.value}>{props.value}</span>
                    )
                })
        }

        return columns
    }

    const columns = useMemo(
        () => columnsFinal(),
        [tenants]
    );

    const updateTableData = (rowIndex, columnId, value) => {
        let isValid = true;
        let error = '';
        if (['RenterPhone', 'OwnerPhone'].includes(columnId)) {
            isValid = value ? BllinkUtils.JsExtras.phoneValidator(null, value, {minLength: 9, maxLength: 10}) : true;
            error = isValid ? '' : t('reports.fieldError', {field: columnId});
        }
        if (['RenterEmail', 'OwnerEmail'].includes(columnId)) {
            isValid = value ? BllinkUtils.JsExtras.emailValidator(value) : true;
            error = isValid ? '' : t('reports.fieldError', {field: columnId});
        }

        setSkipPageReset(true);
        setTableData(old =>
            old.map((row, index) => {
                if (index === rowIndex) {
                    return {
                        ...old[rowIndex],
                        [columnId]: value,
                        errors: {
                            ...row.errors,
                            [columnId]: {
                                touched: true,
                                valid: !error,
                                error: error ? error : undefined
                            }
                        }
                    };
                }
                return row;
            })
        );
    }

    if (!userCheck) {
        return (<div className="div-buffer-empty-content"/>);
    }

    const uploadTenantListFile = async (jsonData) => {
        dispatch(showLoader());
        let payload = {
            tenant_json: JSON.stringify(jsonData),
            building_id: buildingID
        };

        try {
            const result = await client.post(`/managers/tenants/create`, payload);
            const tenantSuccess = result.success && (result.newApartmentsCount > 0 || result.updatedApartmentsCount > 0);
            const tenantNotUpdated = result.success && !tenantSuccess;

            if (tenantSuccess) {
                NotificationManager.success(t('reports.updatedTenantList'))
                setTimeout(() => window.location.reload(), 2000);
            } else if (tenantNotUpdated) {
                NotificationManager.warning(t('reports.notUpdatedTenantList'))
            }
        } catch (e) {
            // NOTE: error will be shown by the client internal error handler
            console.error(e);
        } finally {
            dispatch(hideLoader());
        }
    };

    const handleDrop = async e => {
        const hasFilesToUpload = e.dataTransfer.files && e.dataTransfer.files.length > 0;
        if (hasFilesToUpload) {
            let reader = new FileReader();
            reader.readAsBinaryString(e.dataTransfer.files[0]);

            reader.onload = async function (e) {
                let cfb = XLSX.read(e.target.result, {type: 'binary'});
                let sheetData = cfb.Sheets[cfb.SheetNames[0]];
                let jsonData = XLSX.utils.sheet_to_json(sheetData);
                await uploadTenantListFile(jsonData);
            }
        }
    };

    const parentState = {
        isOneTime: false,
        tenantList: true
    };

    const renderPdfRows = (row, i) => <PdfTenantRow row={row} key={i} />;

    return (
        <StyledTenantsList>
            <PageTitle>
                <PrintPdfDropdown
                    buildingID={buildingID}
                    eventLabel="tenantsList"
                    titleString={t('reports.tenant_list_title')}
                    excelTitle={t('reports.tenant_list_title')}
                    reportName={t('buildings.nav_bar_tenant_list')}
                    pdfContainer={pdfContainer.current}
                    onClick={pdfContainerActivate}
                    rows={tableData}
                    titleRow={titleRow}
                    ignoreColumns={['email', 'errors', 'name', 'phone', 'tenantListData', 'tenantType', 'tenant_type']}
                />
            </PageTitle>

            <div className="ensure-height">
                {(!tableData || tableData.length === 0 || isAdmin) && (
                    <UploadDownloadToolBar
                    parentState={parentState}
                    buildingID={buildingID}
                    onUploadTenantListFile={uploadTenantListFile}
                />
                )}
                {(!tableData || tableData.length === 0) && (
                    <DragAndDrop
                        handleDrop={handleDrop}
                    >
                        <div className={"drag-to-upload-container"}>
                            <div className={"drag-to-upload bllink-font"}>
                                <p>{t('reports.tenant_list_title')}</p>
                                <p>{t('reports.drag_and_drop_info')}</p>
                            </div>
                        </div>
                    </DragAndDrop>
                )}
            </div>

            {tableData && tableData.length > 0 && (
                <StyledActionsBar className={'actions-toolbar align-items-center'}>
                    <div className="control-with-icon search actions-toolbar-search">
                        <input
                            type="text"
                            className="form-control bllink-input-border"
                            name="search"
                            onChange={e => setFilters({
                                global: e.target.value
                            })}
                        />
                        <img src={Search} alt={t('search.search')}/>
                    </div>
                </StyledActionsBar>
            )}

            {tableData && tableData.length > 0 && (
                <Fragment>
                    <Table
                        buildingId={buildingID}
                        columns={columns}
                        data={tableData}
                        filters={filters}
                        updateData={updateTableData}
                        skipPageReset={skipPageReset}
                    />

                    <div className='mt-4 save-btn'>
                        <button
                            className='bllink-button'
                            onClick={saveChanges}
                            disabled={!isEditableTableValid(tableData)}
                        >
                            {t('reports.save')}
                        </button>
                    </div>
                </Fragment>
            )}

            <div id="section-to-print" ref={pdfContainer}>
                {pdfContainerActive && (
                    <StyledPdfWrapper ltr={ltr}>
                        <div className='pdf-title'>{t('reports.tenant_list_title')}</div>

                        <div className='pdf-rows-wrapper'>
                            <div className='pdf-row'>
                                <div className='pdf-highlited yearly-apt'>{t('reports.apartmentName')}</div>
                                <div className='pdf-highlited yearly-apt'>{t('reports.ownerName')}</div>
                                <div className='pdf-highlited yearly-apt'>{t('reports.ownerPhone')}</div>
                                <div className='pdf-highlited yearly-apt'>{t('reports.ownerEmail')}</div>
                                <div className='pdf-highlited yearly-apt'>{t('reports.renterName')}</div>
                                <div className='pdf-highlited yearly-apt'>{t('reports.renterPhone')}</div>
                                <div className='pdf-highlited yearly-apt'>{t('reports.renterEmail')}</div>
                                <div className='pdf-highlited yearly-apt'>{t('reports.monthlyFee')}</div>
                            </div>

                            {tableData && tableData.length > 0 && tableData.map((row, i) => renderPdfRows(row, i))} 
                        </div>
                    </StyledPdfWrapper>
                )}
            </div>
        </StyledTenantsList>
    );
};
